import { useEffect, useMemo, useState } from 'react';
import { Check, X, ZoomIn } from 'lucide-react';

export default function ImageCropper({ file, onCancel, onCrop }) {
    const [zoom, setZoom] = useState(1);
    const [x, setX] = useState(50);
    const [y, setY] = useState(50);
    const [busy, setBusy] = useState(false);
    const url = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);
    useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
    if (!file) return null;

    const finish = async () => {
        setBusy(true);
        try {
            const image = new Image();
            image.src = url;
            await image.decode();
            const size = 1024;
            const canvas = document.createElement('canvas');
            canvas.width = size; canvas.height = size;
            const ctx = canvas.getContext('2d');
            const scale = Math.max(size / image.naturalWidth, size / image.naturalHeight) * zoom;
            const drawW = image.naturalWidth * scale;
            const drawH = image.naturalHeight * scale;
            ctx.drawImage(image, (size - drawW) * (x / 100), (size - drawH) * (y / 100), drawW, drawH);
            const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.84));
            onCrop(new File([blob], 'focused-plant.jpg', { type: 'image/jpeg' }));
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[95] bg-brand-ink/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="image-cropper">
            <div className="bg-brand-bg w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 border border-brand-border">
                <div className="flex items-center justify-between mb-3"><h3 className="font-serif text-xl text-brand-ink">Focus on the plant</h3><button onClick={onCancel}><X size={18} /></button></div>
                <p className="text-xs text-brand-ink3 mb-3">Zoom and move the focus until nearby plants, weeds and background are outside the square.</p>
                <div className="aspect-square overflow-hidden rounded-2xl bg-black border border-brand-border">
                    <img src={url} alt="Crop preview" className="w-full h-full object-cover" style={{ objectPosition: `${x}% ${y}%`, transform: `scale(${zoom})`, transformOrigin: `${x}% ${y}%` }} />
                </div>
                <div className="mt-4 space-y-3">
                    <label className="flex items-center gap-3 text-xs text-brand-ink2"><ZoomIn size={14} /><input className="flex-1" type="range" min="1" max="2.5" step="0.05" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} /></label>
                    <label className="flex items-center gap-3 text-xs text-brand-ink2"><span className="w-14">Left/right</span><input className="flex-1" type="range" min="0" max="100" value={x} onChange={(e) => setX(Number(e.target.value))} /></label>
                    <label className="flex items-center gap-3 text-xs text-brand-ink2"><span className="w-14">Up/down</span><input className="flex-1" type="range" min="0" max="100" value={y} onChange={(e) => setY(Number(e.target.value))} /></label>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-5"><button onClick={onCancel} className="border border-brand-border py-3 rounded-full text-sm">Cancel</button><button onClick={finish} disabled={busy} className="bg-brand-green text-white py-3 rounded-full text-sm inline-flex items-center justify-center gap-2"><Check size={15} /> Use crop</button></div>
            </div>
        </div>
    );
}
